import { useEffect, useState } from "react";
import { Archive, Download, RefreshCw, ShieldCheck, AlertCircle, Database, Eye, ChevronDown, ChevronUp, FileWarning, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";

const fmt = (n) => new Intl.NumberFormat("es-DO").format(Math.round(n || 0));
const fmtDate = (iso) => iso ? new Date(iso).toLocaleString("es-DO", { dateStyle: "short", timeStyle: "short" }) : "—";

export default function ArchiveCard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/archive/stats");
      setStats(r.data);
    } catch (e) {
      toast.error("No se pudo cargar archive stats", { description: formatApiError(e.response?.data?.detail) });
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const snapshot = async () => {
    setSyncing(true);
    const id = toast.loading("Sincronizando archive…");
    try {
      const r = await api.post("/admin/archive/snapshot");
      toast.success(`Snapshot ejecutado en ${r.data.elapsed_ms}ms`, {
        id,
        description: `Nuevos: ${r.data.digital_new} digital · ${r.data.raw_new} raw`,
      });
      load();
    } catch (e) {
      toast.error("Error en snapshot", { id, description: formatApiError(e.response?.data?.detail) });
    } finally {
      setSyncing(false);
    }
  };

  const exportCsv = async (collection) => {
    const id = toast.loading(`Descargando ${collection}_archive.csv…`);
    try {
      const r = await api.get("/admin/archive/export", { params: { collection }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${collection}_archive.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Archive descargado", { id, description: `${collection}_archive.csv` });
    } catch (e) {
      toast.error("Error al descargar", { id, description: formatApiError(e.response?.data?.detail) });
    }
  };

  if (loading || !stats) {
    return (
      <section className="surface p-6" data-testid="card-archive">
        <div className="flex items-center gap-3"><span className="spinner"/> Cargando archive…</div>
      </section>
    );
  }

  const digitalLag = stats.digital.operational_count - stats.digital.archive_count;
  const rawLag = stats.raw.operational_count - stats.raw.archive_count;
  const isHealthy = digitalLag <= 0 && rawLag <= 0;

  return (
    <section className="surface p-6" data-testid="card-archive" style={{ borderLeft: "3px solid #16a34a" }}>
      <div className="flex items-start gap-4 mb-5">
        <div className="kpi-icon-clean" style={{ width: 48, height: 48, color: "#16a34a", background: "#dcfce7", boxShadow: "inset 0 0 0 1px #16a34a3f" }}>
          <Archive size={22}/>
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-3 flex-wrap">
            <h2 className="font-display text-xl font-extrabold">Backup Permanente · Archive</h2>
            <span className={`pill ${isHealthy ? "good" : "bad"} flex items-center gap-1`} style={{ fontSize: 11 }}>
              {isHealthy ? <ShieldCheck size={12}/> : <AlertCircle size={12}/>}
              {isHealthy ? "Sincronizado" : `${digitalLag + rawLag} leads pendientes`}
            </span>
          </div>
          <p className="text-sm text-gray-500 font-semibold mt-1">
            Copia <strong>append-only</strong> de todos los leads recibidos. Protege contra los límites de retención de <strong>Zapier (60d)</strong> y <strong>Meta (90d)</strong>. La data se guarda al primer ingreso y nunca se borra ni sobreescribe — es tu fuente histórica intocable.
          </p>
        </div>
      </div>

      <div className="grid gap-3 mb-5" style={{ gridTemplateColumns: "1fr 1fr" }}>
        <ArchiveStat
          label="Leads Digitales (Zapier · Meta · WPForms)"
          icon={<Database size={14}/>}
          archived={stats.digital.archive_count}
          operational={stats.digital.operational_count}
          lag={digitalLag}
          lastAt={stats.digital.last_archived_at}
          onExport={() => exportCsv("leads_digital")}
        />
        <ArchiveStat
          label="Leads CRM (Dynamics 365 · Power BI)"
          icon={<Database size={14}/>}
          archived={stats.raw.archive_count}
          operational={stats.raw.operational_count}
          lag={rawLag}
          lastAt={stats.raw.last_archived_at}
          onExport={() => exportCsv("leads_raw")}
        />
      </div>

      <div className="flex gap-3 flex-wrap">
        <button className="btn primary flex items-center gap-2" onClick={snapshot} disabled={syncing} data-testid="archive-snapshot-btn">
          {syncing ? <span className="spinner"/> : <RefreshCw size={14}/>}
          Sincronizar ahora
        </button>
        <button className="btn flex items-center gap-2" onClick={load} disabled={syncing}>
          <RefreshCw size={14}/> Refrescar stats
        </button>
      </div>

      <div className="mt-4 p-3 rounded-lg" style={{ background: "#f0fdf4", border: "1px solid #16a34a30" }}>
        <p className="text-xs font-semibold text-gray-700">
          💡 <strong>Comportamiento:</strong> cada nuevo lead que entra (vía webhook Zapier, CSV upload, Power Automate sync) se copia automáticamente al archive con <code className="bg-white px-1 rounded">archived_at</code> = momento del primer ingreso. Re-envíos via dedupe NO actualizan el archive — mantiene la versión original del lead.
        </p>
      </div>

      <DiffViewer/>
    </section>
  );
}


function DiffViewer() {
  const [open, setOpen] = useState(false);
  const [collection, setCollection] = useState("leads_digital");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async (coll = collection) => {
    setLoading(true);
    try {
      const r = await api.get("/admin/archive/diff", { params: { collection: coll, limit: 100 } });
      setData(r.data);
    } catch (e) {
      toast.error("Error al calcular diff", { description: formatApiError(e.response?.data?.detail) });
    } finally {
      setLoading(false);
    }
  };

  const switchCollection = (coll) => {
    setCollection(coll);
    load(coll);
  };

  useEffect(() => {
    if (open && !data) load(collection);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const orphanCount = data?.orphan_count ?? 0;
  const isClean = data && orphanCount === 0;

  return (
    <div className="mt-4" data-testid="archive-diff-viewer">
      <button
        className="w-full flex items-center justify-between p-3 rounded-lg font-bold text-sm"
        style={{
          background: open ? "#0f172a" : "#fafbfc",
          color: open ? "#fff" : "#111",
          border: "1px solid " + (open ? "#1e293b" : "#e5e7eb"),
        }}
        onClick={() => setOpen((v) => !v)}
        data-testid="archive-diff-toggle"
      >
        <span className="flex items-center gap-2">
          <Eye size={16}/> Diff Viewer · Leads protegidos por el archive
          {data && (
            <span
              className="pill flex items-center gap-1"
              style={{
                background: isClean ? "#15803d" : "#dc2626",
                color: "#fff",
                fontSize: 10,
              }}
            >
              {isClean ? <CheckCircle2 size={10}/> : <FileWarning size={10}/>}
              {isClean ? "0 orfanos · todo sincronizado" : `${fmt(orphanCount)} orfanos detectados`}
            </span>
          )}
        </span>
        {open ? <ChevronUp size={16}/> : <ChevronDown size={16}/>}
      </button>

      {open && (
        <div className="mt-2 surface p-4" style={{ background: "#fafbfc" }}>
          <p className="text-xs text-gray-600 font-semibold mb-3">
            Leads que existen en el <strong>archive</strong> pero ya no aparecen en el operativo.
            Estos son los leads que el archive te ha <strong>salvado</strong> — habrían desaparecido si solo dependieras del operativo (Zapier 60d, Meta 90d, wipe accidental por CSV replace, etc.).
          </p>

          {/* Collection switcher */}
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <button
              className="btn flex items-center gap-1"
              style={{ background: collection === "leads_digital" ? "#0f172a" : "#fff", color: collection === "leads_digital" ? "#fff" : "#111", fontSize: 12 }}
              onClick={() => switchCollection("leads_digital")}
              data-testid="diff-tab-digital"
            >
              <Database size={12}/> Leads Digitales
            </button>
            <button
              className="btn flex items-center gap-1"
              style={{ background: collection === "leads_raw" ? "#0f172a" : "#fff", color: collection === "leads_raw" ? "#fff" : "#111", fontSize: 12 }}
              onClick={() => switchCollection("leads_raw")}
              data-testid="diff-tab-raw"
            >
              <Database size={12}/> Leads CRM
            </button>
            <button
              className="btn flex items-center gap-1"
              onClick={() => load(collection)}
              disabled={loading}
              style={{ fontSize: 12 }}
              data-testid="diff-refresh-btn"
            >
              {loading ? <span className="spinner"/> : <RefreshCw size={12}/>}
              {loading ? "Calculando…" : "Recalcular"}
            </button>
            {data && (
              <span className="text-xs text-gray-500 font-semibold ml-auto">
                {data.elapsed_ms}ms · mostrando {data.returned} de {fmt(orphanCount)}
              </span>
            )}
          </div>

          {loading && !data && (
            <div className="text-center py-6 text-gray-500 text-sm font-semibold">
              <span className="spinner inline-block mr-2"/> Comparando archive vs operativo…
            </div>
          )}

          {data && orphanCount === 0 && (
            <div
              className="p-4 rounded-lg text-center"
              style={{ background: "#dcfce7", border: "1px solid #16a34a30" }}
              data-testid="diff-clean-state"
            >
              <ShieldCheck size={28} style={{ color: "#16a34a", margin: "0 auto 6px", display: "block" }}/>
              <div className="font-bold text-sm" style={{ color: "#15803d" }}>
                Archive y operativo sincronizados al 100%
              </div>
              <div className="text-xs font-semibold mt-1" style={{ color: "#15803d99" }}>
                No hay orfanos — toda la data del archive sigue accesible en el operativo.
              </div>
            </div>
          )}

          {data && data.items.length > 0 && (
            <div className="surface overflow-x-auto" style={{ background: "#fff", padding: 0 }} data-testid="diff-orphans-table">
              <table className="w-full text-xs">
                <thead style={{ background: "#fef2f2" }}>
                  <tr className="text-left">
                    {collection === "leads_digital" ? (
                      <>
                        <th className="p-2 font-bold">Nombre</th>
                        <th className="p-2 font-bold">Contacto</th>
                        <th className="p-2 font-bold">Marca</th>
                        <th className="p-2 font-bold">Campaign</th>
                        <th className="p-2 font-bold">Origen</th>
                        <th className="p-2 font-bold">Creado</th>
                        <th className="p-2 font-bold">Archivado</th>
                      </>
                    ) : (
                      <>
                        <th className="p-2 font-bold">Lead ID</th>
                        <th className="p-2 font-bold">Nombre</th>
                        <th className="p-2 font-bold">Marca</th>
                        <th className="p-2 font-bold">Sucursal</th>
                        <th className="p-2 font-bold">Status</th>
                        <th className="p-2 font-bold">Mes</th>
                        <th className="p-2 font-bold">Archivado</th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((it, i) => (
                    <tr key={(it._id || it.lead_id || i)} className="border-t border-gray-100 font-semibold">
                      {collection === "leads_digital" ? (
                        <>
                          <td className="p-2">{[it.first_name, it.last_name].filter(Boolean).join(" ") || "—"}</td>
                          <td className="p-2 text-xs text-gray-600">{it.email || it.phone || "—"}</td>
                          <td className="p-2">{it.brand || "—"}</td>
                          <td className="p-2 truncate" style={{ maxWidth: 200 }} title={it.campaign}>{it.campaign || "—"}</td>
                          <td className="p-2 text-gray-500" style={{ fontSize: 11 }}>{it.source_type || "—"}</td>
                          <td className="p-2 text-gray-500" style={{ fontSize: 11 }}>{fmtDate(it.created_at)}</td>
                          <td className="p-2 text-gray-500" style={{ fontSize: 11 }}>{fmtDate(it.archived_at)}</td>
                        </>
                      ) : (
                        <>
                          <td className="p-2 font-mono text-xs">{(it.lead_id || "").slice(0, 8)}…</td>
                          <td className="p-2">{it.name || "—"}</td>
                          <td className="p-2">{it.brand || "—"}</td>
                          <td className="p-2">{it.branch || "—"}</td>
                          <td className="p-2 text-xs">{it.status || "—"}</td>
                          <td className="p-2 text-gray-500" style={{ fontSize: 11 }}>{it.year_month || "—"}</td>
                          <td className="p-2 text-gray-500" style={{ fontSize: 11 }}>{fmtDate(it.archived_at)}</td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ArchiveStat({ label, icon, archived, operational, lag, lastAt, onExport }) {
  return (
    <div className="surface p-4" style={{ background: "#fafbfc" }}>
      <div className="label-up mb-2 flex items-center gap-1.5">{icon} {label}</div>
      <div className="flex items-baseline gap-3 mb-2">
        <div className="font-display text-3xl font-extrabold">{fmt(archived)}</div>
        <div className="text-xs text-gray-500 font-semibold">archivados</div>
      </div>
      <div className="text-xs text-gray-600 font-semibold space-y-1">
        <div>Operacional: <strong>{fmt(operational)}</strong></div>
        <div>Diferencia: <strong style={{ color: lag > 0 ? "#dc2626" : "#15803d" }}>{lag > 0 ? `+${fmt(lag)} pendientes` : "✓ sincronizado"}</strong></div>
        <div className="text-gray-500">Última escritura: <strong>{fmtDate(lastAt)}</strong></div>
      </div>
      <button className="btn mt-3 w-full flex items-center justify-center gap-2" onClick={onExport} style={{ fontSize: 12 }}>
        <Download size={12}/> Descargar CSV
      </button>
    </div>
  );
}
